import { AppLayout, Flashbar, FlashbarProps, Link, MixedLineBarChartProps, SplitPanel, Tabs } from '@amzn/awsui-components-core';
import React, { useState } from 'react';

import { buildBreadcrumbChain } from 'src/utilities/RouteConfig';
import { HomePageSideNavigationV2 } from '../../side-navigation-v2/HomePageSideNavigationV2';
import { ChartSummaryContext } from '../../generic-components/ai-summary-components/ChartSummaryContext';
import { ChartSummaryPanel } from '../../generic-components/ai-summary-components/ChartSummaryPanel';
import { CostMetricsContext } from './CostMetricsContext';
import { CostMetricsDetailsPage } from './CostMetricsDetailsTab';
import { CostMetricsSummaryContainer, CostMetricsTableData, StatusType } from './CostMetricsInterface';
import { CostMetricsOverviewPage } from './CostMetricsOverview';
import { CostMetricsSelectionFilters } from './CostMetricsSelectionFilters';

export const CostMetricsMainPage: React.FC = () => {
  const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);

  // Chart Summary State
  const [summaries, setSummaries] = useState<Array<{ title: string; content: string; summaryId: string; timestamp: number }>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSplitPanelOpen, setIsSplitPanelOpen] = useState<boolean>(false);

  const addSummary = (title: string, content: string, summaryId = '') => {
    setSummaries((prev) => [{ title, content, summaryId, timestamp: Date.now() }, ...prev]);
  };

  const clearSummaries = () => {
    setSummaries([]);
  };

  const [costMetricsGraphStatus, setCostMetricsGraphStatus] = useState<StatusType>('loading');

  const [costMetricsDetailsTabStatus, setCostMetricsDetailsTabStatus] = useState<StatusType>('loading');
  const [costMetricsDetailsTabData, setCostMetricsDetailsTabData] = useState<any>();

  const [summaryFilters, setSummaryFilters] = useState<string>('');

  const [costMetricsTrendsByMonthGraphData, setCostMetricsTrendsByMonthGraphData] = useState<
    MixedLineBarChartProps.BarDataSeries<string | number | Date>[]
  >([]);
  const [costMetricsTrendsByMonthGraphDataXDomain, setCostMetricsTrendsByMonthGraphDataXDomain] = useState<string[]>([]);
  const [costMetricsTrendsByMonthGraphDataYDomain, setCostMetricsTrendsByMonthGraphDataYDomain] = useState<number[]>([]);

  const [costByProductGraphData, setCostByProductGraphData] = useState<MixedLineBarChartProps.LineDataSeries<string | number | Date>[]>([]);
  const [costByProductGraphDataXDomain, setCostByProductGraphDataXDomain] = useState<string[]>([]);
  const [costByProductGraphDataYDomain, setCostByProductGraphDataYDomain] = useState<number[]>([]);

  const [costByResourceTypeGraphData, setcostByResourceTypeGraphData] = useState<MixedLineBarChartProps.LineDataSeries<string | number | Date>[]>([]);
  const [costByResourceTypeGraphDataXDomain, setcostByResourceTypeGraphDataXDomain] = useState<string[]>([]);
  const [costByResourceTypeGraphDataYDomain, setcostByResourceTypeGraphDataYDomain] = useState<number[]>([]);

  const [costByApplicationGraphData, setCostByApplicationGraphData] = useState<MixedLineBarChartProps.LineDataSeries<string | number | Date>[]>([]);
  const [costByApplicationGraphDataXDomain, setCostByApplicationGraphDataXDomain] = useState<string[]>([]);
  const [costByApplicationGraphDataYDomain, setCostByApplicationGraphDataYDomain] = useState<number[]>([]);

  const [costMetricsDetailsTable, setCostMetricsDetailsTable] = useState<CostMetricsTableData>({} as CostMetricsTableData);
  const [aggregatedCostByProductDetailsTable, setAggregatedCostByProductDetailsTable] = useState<CostMetricsTableData>({} as CostMetricsTableData);
  const [aggregatedCostByAccountDetailsTable, setAggregatedCostByAccountDetailsTable] = useState<CostMetricsTableData>({} as CostMetricsTableData);
  const [aggregatedCostByApplicationsDetailsTable, setAggregatedCostByApplicationsDetailsTable] = useState<CostMetricsTableData>(
    {} as CostMetricsTableData
  );
  const [costMetricsSummaryContainer, setCostMetricsSummaryContainer] = useState<CostMetricsSummaryContainer>({} as CostMetricsSummaryContainer);
  const [originalFilterOptions, setOriginalFilterOptions] = useState<string>('');

  //Function to display flash bar nottifications
  const displayNotification = (status: FlashbarProps.Type, message: string | React.ReactNode, linkMessage?: string) => {
    function addTicketLink() {
      if (linkMessage) {
        return (
          <Link variant="primary" target="_blank" href={linkMessage} color="inverted" fontSize="body-m">
            {' '}
            submit a ticket
          </Link>
        );
      }
    }
    setFlashbarItems([
      {
        type: status,
        content: (
          <>
            {message} 'or ' {addTicketLink()}
          </>
        ),
        dismissible: true,
        dismissLabel: 'Dismiss message',
        onDismiss: () => setFlashbarItems([]),
        id: 'message_1'
      }
    ]);
  };

  return (
    <ChartSummaryContext.Provider
      value={{
        summaries,
        addSummary,
        isLoading,
        setIsLoading,
        isSplitPanelOpen,
        setIsSplitPanelOpen,
        clearSummaries
      }}
    >
      <AppLayout
        contentType="cards"
        navigation={<HomePageSideNavigationV2 />}
        toolsHide={true}
        notifications={<Flashbar items={flashbarItems} />}
        splitPanel={
          <SplitPanel
            header="Chart Summary"
            i18nStrings={{
              preferencesTitle: 'Split panel preferences',
              preferencesPositionLabel: 'Position',
              preferencesPositionDescription: 'Choose the default split panel position.',
              preferencesPositionSide: 'Side',
              preferencesPositionBottom: 'Bottom',
              preferencesConfirm: 'Confirm',
              preferencesCancel: 'Cancel',
              closeButtonAriaLabel: 'Close panel',
              openButtonAriaLabel: 'Open panel',
              resizeHandleAriaLabel: 'Resize split panel'
            }}
            closeBehavior="hide"
          >
            <ChartSummaryPanel />
          </SplitPanel>
        }
        onSplitPanelToggle={({ detail }) => setIsSplitPanelOpen(detail.open)}
        splitPanelPreferences={{ position: 'side' }}
        splitPanelOpen={isSplitPanelOpen}
        splitPanelSize={400}
        maxContentWidth={Number.MAX_VALUE}
        content={
          <>
            <CostMetricsContext.Provider
              value={{
                displayNotification,
                costMetricsGraphStatus,
                setCostMetricsGraphStatus,
                costMetricsDetailsTabStatus,
                setCostMetricsDetailsTabStatus,
                summaryFilters,
                setSummaryFilters,
                costMetricsDetailsTabData,
                setCostMetricsDetailsTabData,
                costMetricsTrendsByMonthGraphData,
                setCostMetricsTrendsByMonthGraphData,
                costMetricsTrendsByMonthGraphDataXDomain,
                setCostMetricsTrendsByMonthGraphDataXDomain,
                costMetricsTrendsByMonthGraphDataYDomain,
                setCostMetricsTrendsByMonthGraphDataYDomain,
                costByProductGraphData,
                setCostByProductGraphData,
                costByProductGraphDataXDomain,
                setCostByProductGraphDataXDomain,
                costByProductGraphDataYDomain,
                setCostByProductGraphDataYDomain,
                costByResourceTypeGraphData,
                setcostByResourceTypeGraphData,
                costByResourceTypeGraphDataXDomain,
                setcostByResourceTypeGraphDataXDomain,
                costByResourceTypeGraphDataYDomain,
                setcostByResourceTypeGraphDataYDomain,
                costByApplicationGraphData,
                setCostByApplicationGraphData,
                costByApplicationGraphDataXDomain,
                setCostByApplicationGraphDataXDomain,
                costByApplicationGraphDataYDomain,
                setCostByApplicationGraphDataYDomain,
                costMetricsDetailsTable,
                setCostMetricsDetailsTable,
                aggregatedCostByProductDetailsTable,
                setAggregatedCostByProductDetailsTable,
                aggregatedCostByAccountDetailsTable,
                setAggregatedCostByAccountDetailsTable,
                aggregatedCostByApplicationsDetailsTable,
                setAggregatedCostByApplicationsDetailsTable,
                costMetricsSummaryContainer,
                setCostMetricsSummaryContainer,
                originalFilterOptions,
                setOriginalFilterOptions
              }}
            >
              <CostMetricsSelectionFilters />
              <Tabs
                tabs={[
                  {
                    label: 'Overview',
                    id: 'cost-metrics-OV',
                    content: <CostMetricsOverviewPage />
                  },
                  {
                    label: 'Details',
                    id: 'cost-metrics-DT',
                    content: <CostMetricsDetailsPage />
                  }
                ]}
              />
            </CostMetricsContext.Provider>
          </>
        }
      />
    </ChartSummaryContext.Provider>
  );
};
